import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useCallback, useId, type ReactNode } from 'react';

import { testIdAttr, testIdPart } from '@/lib/testIds';
import type { AuditChange, AuditEntry } from '@/api/generated';
import { Button } from '@/components/ui/Button';

import { actionPhrase } from './actions';
import { formatMoment, relativeTime } from './dates';
import { fieldLabel, formatValue } from './formatValue';
import {
  ACTOR_KIND_LABELS,
  actorLabelFor,
  showsKindLine,
  organizationLabel,
  targetLabel,
  targetTypeLabel,
} from './words';

interface ActivityDetailDrawerProps {
  /** The entry shown, or null when the drawer is closed. */
  entry: AuditEntry | null;
  /** The changes to show, already stripped of anything this viewer may not see. */
  changes: AuditChange[];
  showOrganization: boolean;
  /** The organization's own view: Activist staff are not named. */
  hideStaffNames?: boolean;
  onClose: () => void;
  /** Move to the next newer or older entry on the page, if there is one. */
  onNewer?: () => void;
  onOlder?: () => void;
  /**
   * Names the drawer for the testing team's tests. Each fact's value is
   * `<testId>-<fact>-value`, each change `<testId>-change-<field>`. See
   * docs/test-ids.md.
   */
  testId: string;
}

/** One before or after value, as text or several lines or raw JSON. */
function Value({ value, field }: { value: unknown; field: string }) {
  const formatted = formatValue(value, field);
  if (formatted.kind === 'text') return <>{formatted.text}</>;
  if (formatted.kind === 'lines') {
    return (
      <>
        {formatted.lines.map((line) => (
          <Box component="span" key={line} sx={{ display: 'block' }}>
            {line}
          </Box>
        ))}
      </>
    );
  }
  return (
    <Box
      component="pre"
      sx={{ m: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
    >
      {formatted.json}
    </Box>
  );
}

/** A label and its value. */
function Fact({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography component="dt" variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" data-testid={testId} sx={{ m: 0 }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * One audit entry, in full: who, when, what, to what, and what changed.
 *
 * A temporary drawer, so it holds focus while open and Escape closes it. That
 * also means the table behind it cannot be clicked, so moving to another entry
 * is done from inside, with Newer and Older, which change what is shown and
 * leave focus on the button that was pressed.
 *
 * There is nothing here to edit, delete or undo. The log records what
 * happened; it is not a place to change it.
 */
export function ActivityDetailDrawer({
  entry,
  changes,
  showOrganization,
  hideStaffNames = false,
  onClose,
  onNewer,
  onOlder,
  testId,
}: ActivityDetailDrawerProps) {
  const headingId = useId();
  const open = entry !== null;

  /*
   * Focus goes to the heading when the drawer opens, so a screen reader starts
   * reading from the top. Done as the heading arrives on the page rather than
   * in an effect: the drawer renders its contents a step after it opens, so
   * an effect would find no heading yet. The heading stays the same element
   * while Newer and Older swap the entry, so a swap does not pull focus off
   * the button somebody is pressing their way along with.
   */
  const focusOnArrival = useCallback((node: HTMLHeadingElement | null) => {
    node?.focus();
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The table puts focus back itself, on the row of the entry last shown -
      // which, after Newer or Older, is not the row that opened the drawer.
      disableRestoreFocus
      // The heading takes focus instead of the drawer's own container, so a
      // screen reader starts with what the entry is. Focus is still held
      // inside the drawer while it is open.
      ModalProps={{ disableAutoFocus: true }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': headingId,
          ...testIdAttr(testId),
          sx: { width: { xs: '100%', sm: 420 }, p: 3 },
        },
      }}
    >
      {entry ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
            <Typography
              id={headingId}
              ref={focusOnArrival}
              tabIndex={-1}
              variant="h6"
              component="h2"
              data-testid={`${testId}-title`}
              sx={{ flex: 1, '&:focus': { outline: 'none' } }}
            >
              {actionPhrase(entry.action)}
            </Typography>
            <IconButton
              aria-label="Close"
              onClick={onClose}
              edge="end"
              data-testid={`${testId}-close-button`}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box component="dl" sx={{ m: 0 }}>
            <Fact label="When" testId={`${testId}-when-value`}>
              <Box component="span" title={relativeTime(entry.occurred_at)}>
                {formatMoment(entry.occurred_at)}
              </Box>
            </Fact>
            <Fact label="Who" testId={`${testId}-who-value`}>
              {actorLabelFor(entry.actor, hideStaffNames)}
              {showsKindLine(entry.actor, hideStaffNames) ? (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' '}
                  · {ACTOR_KIND_LABELS[entry.actor.kind]}
                </Box>
              ) : null}
            </Fact>
            <Fact label="Item" testId={`${testId}-item-value`}>
              {targetLabel(entry)}
              {entry.target.type ? (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' '}
                  · {targetTypeLabel(entry.target.type)}
                </Box>
              ) : null}
            </Fact>
            {showOrganization ? (
              <Fact label="Organization" testId={`${testId}-organization-value`}>
                {organizationLabel(entry)}
              </Fact>
            ) : null}
          </Box>

          <Typography variant="subtitle2" component="h3" sx={{ mt: 2, mb: 1 }}>
            What changed
          </Typography>
          {changes.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid={`${testId}-changes-empty`}
            >
              No field changes recorded for this action.
            </Typography>
          ) : (
            <Box component="dl" data-testid={`${testId}-changes-list`} sx={{ m: 0 }}>
              {changes.map((change) => (
                <Box
                  key={change.field}
                  data-testid={`${testId}-change-${testIdPart(change.field)}`}
                  sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}
                >
                  <Typography component="dt" variant="body2" sx={{ fontWeight: 600 }}>
                    {fieldLabel(change.field)}
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      Before:{' '}
                    </Box>
                    <Value value={change.before} field={change.field} />
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      After:{' '}
                    </Box>
                    <Value value={change.after} field={change.field} />
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
            {/* Marked unavailable rather than disabled at the ends: a disabled
                button drops the focus that was on it, and pressing Newer to
                reach the newest entry would leave the person nowhere. */}
            <Button
              variant="secondary"
              size="sm"
              aria-disabled={!onNewer}
              onClick={() => onNewer?.()}
              data-testid={`${testId}-newer-button`}
              sx={!onNewer ? { opacity: 0.5 } : undefined}
            >
              Newer
            </Button>
            <Button
              variant="secondary"
              size="sm"
              aria-disabled={!onOlder}
              onClick={() => onOlder?.()}
              data-testid={`${testId}-older-button`}
              sx={!onOlder ? { opacity: 0.5 } : undefined}
            >
              Older
            </Button>
          </Box>
        </>
      ) : null}
    </Drawer>
  );
}
